import React from "react";

import { FieldPath, FieldValues } from "react-hook-form";
import { InputBaseComponentProps } from "@mui/material";
import TextField, { OutlinedTextFieldProps } from "@mui/material/TextField";

import ControllerInput, { ControllerInputProps } from "../ControllerInput/ControllerInput";

export type TextInputProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<ControllerInputProps<TFieldValues, TName>, "render"> &
    Omit<OutlinedTextFieldProps, "variant"> &
    Partial<Pick<InputBaseComponentProps, "maxLength">>;

const TextInput = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    name,
    control,
    type,
    InputProps,
    defaultValue = null,
    FormControlProps,
    LabelProps,
    hideErrorMessage,
    label,
    fullWidth,
    required = false,
    maxLength,
    ...textFieldProps
}: TextInputProps<TFieldValues, TName>): React.ReactElement => {
    return (
        <ControllerInput
            control={control}
            name={name}
            defaultValue={defaultValue}
            label={label}
            LabelProps={{
                ...LabelProps,
                ...(textFieldProps.disabled !== undefined ? { disabled: textFieldProps.disabled } : {}),
            }}
            FormControlProps={FormControlProps}
            required={required}
            fullWidth={fullWidth}
            hideErrorMessage={hideErrorMessage}
            render={({ field: { onChange, onBlur, value, ref }, error }) => (
                <TextField
                    value={!value && value !== 0 ? "" : value}
                    id={name}
                    name={name}
                    inputRef={ref}
                    onChange={onChange}
                    fullWidth={fullWidth}
                    onBlur={(e) => {
                        if (type === "number") {
                            onChange(e.target.value !== "" ? Number(e.target.value) : null);
                        } else {
                            onChange(e.target.value.trim());
                        }
                        onBlur();
                    }}
                    required={required}
                    type={type}
                    error={!!error}
                    {...textFieldProps}
                    InputProps={{
                        ...InputProps,
                        inputProps: {
                            name,
                            maxLength,
                            ...InputProps?.inputProps,
                            "data-testid": `text_input-${name}`,
                        },
                    }}
                />
            )}
        />
    );
};

export default TextInput;
